import { wrapArray } from 'gleam-shared/helpers/nodash'
import { writeDom, cancelFastDom } from 'gleam-shared/browser/fastDom'
import { setState, setErrorState, setLoadedState } from 'gleam-shared/browser/whenStateChanges'

import Enqueue from 'gleam-shared/promises/enqueue'
import Emitter from 'gleam-shared/helpers/emitter'
import Watchdog from 'gleam-shared/browser/watchdog'
import onceIntersects from 'gleam-shared/browser/onceIntersects'

import { showError, showTimeout, showSpinner, hidePlaceholder, removePlaceholder } from './placeholder'

import loadWidget, {
  SELECTOR as WIDGET_SELECTOR,
  NEEDS_PLACEHOLDER as WIDGET_NEEDS_PLACEHOLDER,
  AWAITS_USER_ACTION as WIDGET_AWAITS_USER_ACTION } from './loadWidget'

import loadCaptureEmbed, {
  SELECTOR as CAPTURE_EMBED_SELECTOR,
  NEEDS_PLACEHOLDER as CAPTURE_EMBED_NEEDS_PLACEHOLDER,
  AWAITS_USER_ACTION as CAPTURE_EMBED_AWAITS_USER_ACTION } from './loadCaptureEmbed'

import loadCaptureButton, {
  SELECTOR as CAPTURE_BUTTON_SELECTOR,
  NEEDS_PLACEHOLDER as CAPTURE_BUTTON_NEEDS_PLACEHOLDER,
  AWAITS_USER_ACTION as CAPTURE_BUTTON_AWAITS_USER_ACTION } from './loadCaptureButton'

import loadCapturePreview, {
  SELECTOR as CAPTURE_PREVIEW_SELECTOR,
  NEEDS_PLACEHOLDER as CAPTURE_PREVIEW_NEEDS_PLACEHOLDER,
  AWAITS_USER_ACTION as CAPTURE_PREVIEW_AWAITS_USER_ACTION } from './loadCapturePreview'

import loadGalleryEmbed, {
  SELECTOR as GALLERY_EMBED_SELECTOR,
  NEEDS_PLACEHOLDER as GALLERY_EMBED_NEEDS_PLACEHOLDER,
  AWAITS_USER_ACTION as GALLERY_EMBED_AWAITS_USER_ACTION } from './loadGalleryEmbed'

import loadGalleryInclude, {
  SELECTOR as GALLERY_INCLUDE_SELECTOR,
  NEEDS_PLACEHOLDER as GALLERY_INCLUDE_NEEDS_PLACEHOLDER,
  AWAITS_USER_ACTION as GALLERY_INCLUDE_AWAITS_USER_ACTION } from './loadGalleryInclude'

import loadIframelyMedia, {
  SELECTOR as IFRAMELY_MEDIA_SELECTOR,
  NEEDS_PLACEHOLDER as IFRAMELY_MEDIA_NEEDS_PLACEHOLDER,
  AWAITS_USER_ACTION as IFRAMELY_MEDIA_AWAITS_USER_ACTION } from './loadIframelyMedia'

const LOADERS = [{
  loader: loadWidget,
  selector: WIDGET_SELECTOR,
  needsPlaceholder: WIDGET_NEEDS_PLACEHOLDER,
  awaitsUserAction: WIDGET_AWAITS_USER_ACTION
}, {
  loader: loadCaptureEmbed,
  selector: CAPTURE_EMBED_SELECTOR,
  needsPlaceholder: CAPTURE_EMBED_NEEDS_PLACEHOLDER,
  awaitsUserAction: CAPTURE_EMBED_AWAITS_USER_ACTION
}, {
  loader: loadCaptureButton,
  selector: CAPTURE_BUTTON_SELECTOR,
  needsPlaceholder: CAPTURE_BUTTON_NEEDS_PLACEHOLDER,
  awaitsUserAction: CAPTURE_BUTTON_AWAITS_USER_ACTION
}, {
  loader: loadCapturePreview,
  selector: CAPTURE_PREVIEW_SELECTOR,
  needsPlaceholder: CAPTURE_PREVIEW_NEEDS_PLACEHOLDER,
  awaitsUserAction: CAPTURE_PREVIEW_AWAITS_USER_ACTION
}, {
  loader: loadGalleryEmbed,
  selector: GALLERY_EMBED_SELECTOR,
  needsPlaceholder: GALLERY_EMBED_NEEDS_PLACEHOLDER,
  awaitsUserAction: GALLERY_EMBED_AWAITS_USER_ACTION
}, {
  loader: loadGalleryInclude,
  selector: GALLERY_INCLUDE_SELECTOR,
  needsPlaceholder: GALLERY_INCLUDE_NEEDS_PLACEHOLDER,
  awaitsUserAction: GALLERY_INCLUDE_AWAITS_USER_ACTION
}, {
  loader: loadIframelyMedia,
  selector: IFRAMELY_MEDIA_SELECTOR,
  needsPlaceholder: IFRAMELY_MEDIA_NEEDS_PLACEHOLDER,
  awaitsUserAction: IFRAMELY_MEDIA_AWAITS_USER_ACTION
}]

const QUEUE_SIZE = 1
const ROOT_MARGIN = '0px'
const EMBED_SELECTOR = LOADERS.map(({ selector }) => selector).join(', ')

export const TIMEOUT_ATTR = 'data-timeout'
export const TIMEOUT_INTERVAL_ATTR = 'data-timeout-interval'
const DEFAULT_TIMEOUT_INTERVAL = 5_000

export default function LazyEmbedQueue(root) {
  const embeds = new Set()
  const sleepers = new Map()

  const enqueue = Enqueue(QUEUE_SIZE)
  const errorEmitter = Emitter()

  for (const { el, loader, needsPlaceholder, awaitsUserAction } of queryAll(root)) {
    const embed = LazyEmbed(el, loader, needsPlaceholder, awaitsUserAction)
    const observer = onceIntersects(el, ROOT_MARGIN, onIntersect)
    embeds.add(embed)
    sleepers.set(el, { embed, observer, awaitsUserAction })
  }

  return { destroy, errorEmitter }

  function destroy() {
    for (const embed of embeds.values()) {
      embed.destroy()
    }

    for (const { observer } of sleepers.values()) {
      observer.destroy()
    }
  }

  function onIntersect(el) {
    const { embed, awaitsUserAction } = sleepers.get(el)

    sleepers.delete(el)

    if (awaitsUserAction) {
      embed.load(errorEmitter.emit)
    } else {
      enqueue(() => {
        return new Promise(embed.load)
      })
    }
  }
}

function LazyEmbed(el, loader, needsPlaceholder, awaitsUserAction) {
  let error = null
  let timeout = null
  let preview = null
  let callback = null
  let watchdog = null

  if (!awaitsUserAction) {
    writeDom(lazyInit)
  }

  return { load, destroy }

  function destroy() {
    cancelFastDom(lazyInit)
    cancelFastDom(lazyFinalize)
    cancelFastDom(lazyShowError)
    cancelFastDom(lazyShowTimeout)
    cancelFastDom(lazyHidePlaceholder)

    unsubscribe()
    preview?.destroy()
    watchdog?.destroy()
  }

  function unsubscribe() {
    if (preview) {
      preview.loadEmitter.off(onLoad)
      preview.errorEmitter.off(onError)
      preview.errorEmitter.off(callback)
    }
  }

  function load(cb) {
    preview = loader(el)
    callback = cb
    console.info('LazyEmbed: loading', preview.description)

    if (awaitsUserAction) {
      preview.errorEmitter.on(callback)
    } else {
      watchdog = Watchdog(getTimeoutInterval(el), preview.description, onTimeout)

      preview.loadEmitter.on(onLoad)
      preview.errorEmitter.on(onError)
    }
  }

  function onLoad() {
    unsubscribe()
    watchdog.destroy()

    if (needsPlaceholder) {
      writeDom(lazyHidePlaceholder)
    } else {
      writeDom(lazyFinalize)
    }

    callback()
  }

  function onError(err) {
    error = err
    unsubscribe()
    preview.destroy()
    watchdog.destroy()
    writeDom(lazyShowError)

    callback()
  }

  function onTimeout(err) {
    timeout = err
    writeDom(lazyShowTimeout)

    callback()
  }

  function onPlaceholderHidden() {
    writeDom(lazyFinalize)
  }

  function lazyInit() {
    if (needsPlaceholder) {
      showSpinner(el)
    } else {
      loader(el, true)
    }
  }

  function lazyHidePlaceholder() {
    hidePlaceholder(el, onPlaceholderHidden)
  }

  function lazyFinalize() {
    removePlaceholder(el)
    setLoadedState(el)
  }

  function lazyShowError() {
    showError(el, error)
    setErrorState(el, error)
  }

  function lazyShowTimeout() {
    showTimeout(el, timeout)
    setState(el, TIMEOUT_ATTR, timeout)
  }
}

function getTimeoutInterval(el) {
  return el.getAttribute(TIMEOUT_INTERVAL_ATTR) ?? DEFAULT_TIMEOUT_INTERVAL
}

function queryAll(root) {
  // querying elements while preserving their order
  return wrapArray(root.querySelectorAll(EMBED_SELECTOR)).map((el) => {
    for (const { loader, selector, needsPlaceholder, awaitsUserAction } of LOADERS) {
      if (el.matches(selector)) {
        return { el, loader, needsPlaceholder, awaitsUserAction }
      }
    }

    throw new Error('should have found a match for the el!')
  })
}
